/* Pouch frames hero. Vanilla, no dependency.
   Plays the section's Frame blocks as a scroll-scrubbed sequence on a canvas.
   Timeline (progress 0 → 1 over the pinned scroll):
   Phases (each its own scroll length, skipped if its block is missing):
     frames   0.00–0.05 scroll hint fades; 0.03–0.10 tagline lifts away (before the
              camera moves, so it never sits over a moving pouch); 0.10–1.00 frames
              play in block order with motion blur, camera push/sway/tilt and the
              end-framing sink
     cards    Info cards slide in beside the pouch (desktop: all in by 60%, then
              hold; phones: one at a time)
   Only those two phases are pinned. After them the hero is a normal page: the
   Reviews and Closing button blocks are moved into the flow below the pinned
   stage, the info cards scroll away like page content, and the stage stays behind
   everything as the background while the pouch drifts a little and eases to a stop. */
(function () {
  var instances = new WeakMap();

  /* Frame geometry, as fractions of the frame canvas height. The canvas shape
     comes from the frames themselves (data-hfs-ar); this is only the fallback. */
  var FRAME_AR = 1080 / 1920; // width / height
  var MOUTH_Y = 0.509; // pouch mouth height in the frames (measured)
  var OPENING_TOP_Y = 0.461; // top edge of the open mouth in the first frame (measured)
  /* Layout: how big the frame is drawn, and where the mouth sits on screen. */
  var FRAME_H = 1.2; // frame height as a multiple of the screen height
  var MOUTH_SCREEN = 0.56; // mouth position, fraction of screen height
  /* Timing. */
  var FRAMES_FROM = 0.1;
  /* Camera presets (section setting): push-in share, sideways sway as a share of
     the screen width, tilt in degrees. */
  var CAMERA = {
    still: { push: 0, sway: 0, tilt: 0 },
    subtle: { push: 0.05, sway: 0.01, tilt: 0.6 },
    dynamic: { push: 0.16, sway: 0.03, tilt: 2.2 }
  };
  /* Each frame holds on its own for most of its scroll distance and only blends
     into the next in the middle BLEND share. Consecutive spill frames differ by
     position, so a full-length blend shows faded double berries. */
  var BLEND = 0.3;
  var MAX_SAMPLES = 4; // motion blur: most frame samples per mix (bounds the cost)
  var MAX_PX = 2400000; // canvas pixel budget, desktop
  var MAX_PX_PHONE = 1600000; // canvas pixel budget, phones
  /* Frame resolution asked from Shopify's CDN: the drawn size on this screen,
     rounded up to a step (so visitors share CDN cache), and capped so all
     decoded frames stay within a phone's memory. */
  var WIDTH_STEPS = [810, 1080, 1620, 2160, 2700, 3240];
  var MAX_W = 2160; // desktop
  var MAX_W_PHONE = 1080; // screens narrower than 768 px

  function clamp(v) { return !(v > 0) ? 0 : v > 1 ? 1 : v; } // NaN -> 0
  function seg(p, a, b) { return clamp((p - a) / (b - a)); }
  function easeInOut(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

  function reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function mount(root) {
    if (instances.has(root)) return;

    /* Scopes the scroll/sticky overflow override in the CSS to pages that
       actually contain this hero. */
    document.documentElement.classList.add('hfs-host');

    var camera = root.querySelector('[data-hfs-camera]');
    var canvas = root.querySelector('[data-hfs-canvas]');
    var poster = root.querySelector('.hfs__poster');
    var tag = root.querySelector('[data-hfs-tag]');
    var anchored = !!(tag && tag.hasAttribute('data-hfs-anchor')); // follow the pouch opening; other positions are pure CSS
    var hint = root.querySelector('[data-hfs-hint]');
    /* Frames come from the Frame blocks' own data, in page (= block) order. */
    var frameEls = Array.prototype.slice.call(root.querySelectorAll('[data-hfs-frame]'));
    // Phones get the smaller frames (data-src-m): the canvas can't show more, and decoded frames stay in memory.
    var phoneFrames = window.innerWidth < 750;
    var urls = frameEls.map(function (el) { return (phoneFrames && el.getAttribute('data-src-m')) || el.getAttribute('data-src'); });
    if (!canvas || !camera || !urls.length) {
      /* Nothing to animate (e.g. no frames picked yet): show the cards, reviews and
         button as a normal stacked page rather than leaving them invisible. */
      root.classList.add('hfs--static');
      root.classList.add('hfs--ready');
      instances.set(root, { destroy: function () { root.classList.remove('hfs--static'); } });
      return;
    }
    var firstAr = parseFloat(frameEls[0].getAttribute('data-ar'));
    if (firstAr > 0) {
      root.setAttribute('data-hfs-ar', firstAr);
      root.style.setProperty('--hfs-ar', firstAr);
    }
    var ctx = canvas.getContext('2d');
    var ar = parseFloat(root.getAttribute('data-hfs-ar')) || FRAME_AR;
    /* Section settings: pouch size, opening height, camera movement. */
    var frameHD = (parseFloat(root.getAttribute('data-hfs-size')) || FRAME_H * 100) / 100;
    var frameHM = (parseFloat(root.getAttribute('data-hfs-size-mobile')) || frameHD * 100) / 100; // Pouch size (phone)
    var mouthScreen = (parseFloat(root.getAttribute('data-hfs-pouch-y')) || MOUTH_SCREEN * 100) / 100;
    var mouthPhone = (parseFloat(root.getAttribute('data-hfs-pouch-y-mobile')) || 50) / 100; // Pouch opening height (phone)
    var svhProbe = null;
    function smallScreenH() { // the visible height with the toolbars showing (as when the page opens)
      if (!svhProbe) { svhProbe = document.createElement('div'); svhProbe.style.cssText = 'position:fixed;top:0;left:-9999px;width:1px;height:100vh;height:100svh;visibility:hidden;pointer-events:none'; document.body.appendChild(svhProbe); }
      var h = Math.min(svhProbe.offsetHeight || window.innerHeight, window.innerHeight);
      if (touchPhone()) { if (!heroSmall) heroSmall = h; return heroSmall; } // fixed per width (reset with the width)
      return h;
    }
    var cam = CAMERA[root.getAttribute('data-hfs-camera-mode')] || CAMERA.dynamic;
    var endShiftD = (parseFloat(root.getAttribute('data-hfs-end-shift')) || 0) / 100;
    var endShiftAttrM = root.getAttribute('data-hfs-end-shift-mobile');
    var endShiftM = endShiftAttrM === null || endShiftAttrM === '' ? endShiftD : (parseFloat(endShiftAttrM) || 0) / 100; // End framing (phone); 0 is a real value
    var endShift = endShiftD; // the one in use: chosen per screen in measure()

    /* Phases, in screen-heights of scrolling: frames, info cards, reviews, closing
       button. A phase without its block takes no scroll at all. The track height is
       set here so it always matches what's actually on the page. */
    var cardsEl = root.querySelector('[data-hfs-cards]');
    var cardEls = cardsEl ? Array.prototype.slice.call(cardsEl.querySelectorAll('[data-hfs-card]')) : [];
    var reviewsEl = root.querySelector('[data-hfs-reviews]');
    var ctaEl = root.querySelector('[data-hfs-cta]');
    /* Pinned lengths in screen heights (desktop, phone), real scrolling distance.
       Read per screen size in measure(), so turning a tablet re-applies the right one. */
    var DEF = [100, 90, 90, 120]; // used if a length is missing (e.g. an older section file)
    var lens = (root.getAttribute('data-hfs-lengths') || '').split(',').map(function (v, i) { var n = parseFloat(v); return n > 0 ? n : DEF[i]; });
    for (var li = lens.length; li < 4; li++) lens[li] = DEF[li];
    var L = { frames: 1, cards: 0 };
    function lengths() {
      var phone = window.innerWidth < 750;
      L.frames = Math.max(40, phone ? lens[1] : lens[0]) / 100;
      L.cards = cardEls.length ? (phone ? lens[3] : lens[2]) / 100 : 0;
      /* The pinned distance is laid out in CSS vh, which on phones stays put while the
         browser's address bar slides in and out (window.innerHeight doesn't). */
      root.style.setProperty('--hfs-pin', (L.frames + L.cards) * 100);
    }
    /* Pixel size of 1vh as CSS lays it out (a probe, since innerHeight differs on phones).
       One shared probe on <html>, never inside the hero: the theme editor restarts the
       hero whenever its contents change, and a probe added on every start would make
       that an endless restart loop (the flicker that kept replaying the animation). */
    var probe = document.querySelector('[data-hfs-probe]');
    if (!probe) {
      probe = document.createElement('div');
      probe.setAttribute('data-hfs-probe', '');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText = 'position:absolute;left:0;top:0;width:1px;height:100vh;visibility:hidden;pointer-events:none';
      document.documentElement.appendChild(probe);
    }
    /* Smoothing 0-90%: the share of the remaining distance the frames cover per screen
       refresh. Only the frame scrub is smoothed; everything after the pinned part
       follows the page directly. */
    var CHASE = 1 - Math.min(90, Math.max(0, parseFloat(root.getAttribute('data-hfs-smooth')) || 0)) / 100;
    /* Gentle pouch drift after the cards: distance (vh, + down / - up) and how much scrolling it takes. */
    var dr = (root.getAttribute('data-hfs-drift') || '10,80,bottom,96').split(',');
    var driftVh = (parseFloat(dr[0]) || 0) / 100, driftOver = Math.max(0.2, (parseFloat(dr[1]) || 80) / 100);
    var toBottom = (dr[2] || 'bottom') !== 'drift', bottomAt = Math.min(1, Math.max(0.6, (parseFloat(dr[3]) || 96) / 100));
    var exitVh = Math.max(0, parseFloat(root.getAttribute('data-hfs-exit')) || 0) / 100;
    var spacer = root.querySelector('[data-hfs-spacer]');
    var flow = root.querySelector('[data-hfs-flow]');
    var cardsAround = !!(cardsEl && cardsEl.classList.contains('hfs__cards--around'));
    /* Tilt, column and stagger by real order. CSS position rules (nth-child) would
       be thrown off by wrappers Shopify puts around blocks or by hidden elements
       between cards, which is what made every card lean the same way. */
    var TILT = [-1.6, 1.2, 1.4, -1], STAGGER = ['-3vh', '2vh', '-1vh', '1.5vh'];
    cardEls.forEach(function (c, i) {
      c.style.setProperty('--hfs-tilt', TILT[i % 4]);
      var item = c;
      while (item.parentElement && item.parentElement !== cardsEl) item = item.parentElement;
      if (cardsAround) {
        item.style.setProperty('--hfs-col', i % 2 === 0 ? 1 : 3);
        item.style.setProperty('--hfs-stagger', STAGGER[i % 4]);
      }
    });
    /* The tagline: the overlay's children that are neither a phase layer nor a frame
       (nor a wrapper around one). Only these fade when scrolling starts. */
    var tagEls = tag ? Array.prototype.slice.call(tag.children).filter(function (c) {
      if (/^(STYLE|SCRIPT|LINK|TEMPLATE)$/.test(c.tagName)) return false;
      var NOT_TAG = '.hfs__layer, [data-hfs-frame], [data-hfs-reviews], [data-hfs-cta]';
      if (c.matches(NOT_TAG)) return false;
      return !c.querySelector(NOT_TAG);
    }) : [];
    tagEls.forEach(function (c) { c.setAttribute('data-hfs-tagline', ''); });
    /* Everything else in the overlay that isn't itself a layer (e.g. a wrapper Shopify
       puts around one) leaves the flow, so it can't push the tagline up. */
    if (tag) Array.prototype.forEach.call(tag.children, function (c) {
      if (tagEls.indexOf(c) < 0 && !c.matches('.hfs__layer') && !/^(STYLE|SCRIPT|LINK|TEMPLATE)$/.test(c.tagName)) c.classList.add('hfs__out');
    });
    var cardsLeft = !!(cardsEl && cardsEl.classList.contains('hfs__cards--left'));
    var frames = urls.map(function () { return null; }); // decoded images, filled as they load
    var staticMode = reduceMotion() || (window.innerWidth < 750 && root.getAttribute('data-hfs-phone-still') === 'true'); // Still image on phones
    /* Reviews and the closing button are normal page content: move them (or the wrapper
       Shopify put around them) out of the pinned stage into the flow below it. */
    if (!staticMode && flow && tag) [reviewsEl, ctaEl].forEach(function (el) {
      if (!el || !tag.contains(el)) return;
      var top = el;
      while (top.parentElement && top.parentElement !== tag) top = top.parentElement;
      top.classList.remove('hfs__out');
      /* Normal content now. An older Reviews/Closing button block still carries the
         pinned-layer class (hidden until a script fades it in), plus any inline styles
         the old engine left: drop both, or the block stays invisible. */
      el.classList.remove('hfs__layer', 'is-live');
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
      flow.appendChild(top);
    });
    if (flow) flow.classList.toggle('hfs__flow--used', !!flow.children.length);
    /* Phones, "Info cards move: With the page": the card column leaves the pinned stage and
       sits in the normal-page part at a fixed spot, so it scrolls exactly like page content
       over the pinned pouch. Nothing positions it per frame, so it can't land a frame behind
       the finger (on iPhones the stage-bound column jumped ~15 px at the start and end of every
       swipe). The cards stretch then lasts exactly as long as the column takes to pass (see
       measure()); "Timed" keeps the earlier stage-bound column and its set length. */
    var colPage = root.getAttribute('data-hfs-col-motion') !== 'timed';
    var flowUsed = !!(flow && flow.classList.contains('hfs__flow--used'));
    var colHost = cardsEl ? cardsEl.closest('[data-hfs-colhost]') : null; // from an earlier start-up
    var cardsTop = cardsEl;
    if (cardsEl) { var colStop = colHost || tag; while (cardsTop.parentElement && cardsTop.parentElement !== colStop) cardsTop = cardsTop.parentElement; }
    var colPageNow = false;
    function colWanted() { return colPage && !staticMode && flowUsed && !!(cardsEl && tag && cardEls.length) && window.innerWidth < 750; }
    function placeColumn() {
      if (!cardsEl || !tag) return;
      var want = colWanted();
      if (want) {
        if (!colHost) { colHost = document.createElement('div'); colHost.className = 'hfs__colhost'; colHost.setAttribute('data-hfs-colhost', ''); }
        if (colHost.parentElement !== flow) flow.appendChild(colHost); // inside the flow: the editor watcher ignores the hero's own moves there
        if (cardsTop.parentElement !== colHost) colHost.appendChild(cardsTop);
      } else if (colHost && cardsTop.parentElement === colHost) {
        tag.appendChild(cardsTop); // back onto the stage (wider screen, or Timed)
        colHost.remove(); colHost = null;
      }
      if (want === colPageNow) return;
      colPageNow = want;
      root.classList.toggle('hfs--col-page', want);
      /* Drop what the other mode wrote inline, so nothing stale stays on the cards. */
      cardsEl.style.translate = ''; cardsEl.style.opacity = '';
      cardEls.forEach(function (el) {
        el._hfsLast = null; el.style.transform = '';
        var wrap = el.parentElement !== cardsEl ? el.parentElement : null;
        if (wrap) { wrap.style.translate = ''; wrap._hfsY = null; }
      });
      lastExit = null;
    }

    var vw = 0, vh = 0, boxW = 0, boxH = 0, boxTop = 0, dpr = 1, px = 1, cardsH = 0;
    var target = 0, cur = 0, raf = 0, drawnKey = '', started = false, renderedAt = -1;
    var pinPx = 0, flowPx = 1, unit = 1; // pinned scroll distance and the normal-page part after it (px); px per vh
    var heroW = 0, heroH = 0, heroSmall = 0; // touch phones: the fixed hero screen height per width (see measure)
    function touchPhone() { return !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches); }
    /* Motion blur state and settings (strength 0-100 from the section). */
    var tagFit = 1; // tagline scale needed to fit above the opening (1 = natural size)
    /* Words stay whole: if a word is wider than its line, the tagline's type shrinks (5% steps,
       down to 40%) until every word fits. Font size, not scale, so the lines reflow; the fitting
       above the opening (tagFit, a scale) comes after, from the new height. */
    function fitTagWords() {
      tagEls.forEach(function (tg) {
        var els = Array.prototype.slice.call(tg.querySelectorAll('h1, h2, h3, h4, h5, h6, p'));
        if (!els.length) els = [tg];
        els.forEach(function (el) { el.style.fontSize = ''; });
        var tooWide = function () { return els.some(function (el) { return el.scrollWidth > el.clientWidth + 1; }); };
        if (!tooWide()) return;
        var base = els.map(function (el) { return parseFloat(getComputedStyle(el).fontSize) || 16; });
        for (var k = 0.95; k >= 0.4; k -= 0.05) {
          els.forEach(function (el, i) { el.style.fontSize = (base[i] * k).toFixed(1) + 'px'; });
          if (!tooWide()) break;
        }
      });
    }
    var velS = 0, lastPos = -1, drawMs = 0, lastCut = 0; // -1: no previous position yet (no blur on the first draw)
    var blurGain = (parseFloat(root.getAttribute('data-hfs-blur-strength')) || 0) / 100 * 6;
    var maxSpan = window.innerWidth < 768 ? 1.4 : 2.4;

    /* Review grid: its width is a share of the visible page width (clientWidth, which,
       unlike vw, excludes the scrollbar), centred on the screen whatever offsets its
       container. Without this a 96vw grid ran off the right edge. */
    var gridTrack = reviewsEl ? reviewsEl.querySelector('.rc__track') : null;
    function placeGrid() {
      if (!gridTrack || staticMode) return;
      var cs = getComputedStyle(reviewsEl);
      var pct = parseFloat(cs.getPropertyValue('--hfs-rv-w')) || 96;
      var cap = parseFloat(cs.getPropertyValue('--hfs-rv-max')) || Infinity;
      var cw = document.documentElement.clientWidth;
      var w = cw < 750 ? cw - 28 : Math.min(cw * pct / 100, cap);
      gridTrack.style.width = Math.round(w) + 'px';
      gridTrack.style.maxWidth = 'none';
      gridTrack.style.marginLeft = '0px';
      /* Layout position, not the on-screen one: an animation, a page scrolled a little
         sideways or a scrollbar arriving late at this moment used to leave the grid
         off-centre (to the right) for the whole visit. */
      var left = layoutLeft(gridTrack.parentElement);
      gridTrack.style.marginLeft = Math.round((cw - w) / 2 - left) + 'px';
      /* Check, and correct once if something (e.g. a collapsing margin) still offsets it. */
      var off = layoutLeft(gridTrack) - (cw - w) / 2;
      if (Math.abs(off) > 1) gridTrack.style.marginLeft = Math.round(parseFloat(gridTrack.style.marginLeft) - off) + 'px';
      placedCw = cw;
      fitWords();
      markRows();
    }
    /* An element's left edge in the page layout: transforms and horizontal scrolling ignored. */
    function layoutLeft(el) {
      var x = 0;
      for (var n = el; n; n = n.offsetParent) {
        x += n.offsetLeft;
        if (getComputedStyle(n).position === 'fixed') break; // fixed: already relative to the screen
      }
      return x;
    }
    var placedCw = -1;
    /* Re-centre whenever the usable width or the layout may have settled differently. */
    function replace() { if (instances.get(root)) placeGrid(); }
    /* Brand touches on the review cards, by real card order (Shopify may wrap blocks):
       palette tints, corner icons, the decorative quotation mark. Idempotent, so an
       editor re-render can run it again. */
    var rvCards = gridTrack ? Array.prototype.slice.call(gridTrack.querySelectorAll('[data-rc-card]')) : [];
    function decorateReviews() {
      if (!reviewsEl || !rvCards.length) return;
      /* Card style: the card's own choice (Card → In the home hero), else the block's. */
      var base = ((reviewsEl.className.match(/hfs__reviews--style-(\w+)/) || [])[1]) || 'paper';
      var pal = (reviewsEl.getAttribute('data-hfs-rv-palette') || 'off').split('|');
      var mode = pal[0], cols = pal.slice(1).filter(Boolean);
      var mark = reviewsEl.classList.contains('hfs__reviews--mark');
      rvCards.forEach(function (card, i) {
        var own = card.getAttribute('data-hfs-card-style');
        card.setAttribute('data-hfs-style', own && own !== 'default' ? own : base);
        /* Palette tints only for cards without their own Background colour. */
        card.style.removeProperty('--hfs-rv-pal');
        if (cols.length && (mode === 'all' || (mode === 'some' && i % 3 === 1))) {
          card.style.setProperty('--hfs-rv-pal', cols[(mode === 'all' ? i : (i - 1) / 3) % cols.length]);
        }
        card.querySelectorAll('.hfs-rv-icon, .hfs-rv-mark').forEach(function (x) { x.remove(); });
        /* The card's own icon: url~position~tilt~overhang%~size (0 = the block's size). */
        var rec = card.getAttribute('data-hfs-icon');
        if (rec) {
          var f = rec.split('~'), size = parseFloat(f[4]) || 0;
          var sz = size > 0 ? size + 'px' : 'var(--hfs-rv-icon,56px)';
          var sp = document.createElement('span');
          sp.className = 'hb-icon hb-icon--' + (f[1] || 'top-left') + ' hfs-rv-icon';
          sp.setAttribute('aria-hidden', 'true');
          sp.style.cssText = '--hb-icon-size:' + sz + ';--hb-icon-inset:calc(' + sz + ' * ' + (-(parseFloat(f[3]) || 0) / 100) + ');--hb-icon-rot:' + (parseFloat(f[2]) || 0) + 'deg';
          var im = document.createElement('img');
          im.className = 'hb-icon__img'; im.alt = ''; im.src = f[0];
          sp.appendChild(im); card.appendChild(sp);
        }
        if (mark) {
          var m = document.createElement('span');
          m.className = 'hfs-rv-mark'; m.setAttribute('aria-hidden', 'true'); m.textContent = '\u201D';
          card.appendChild(m);
        }
      });
    }
    /* Rows for the parallax: odd rows drift the other way. offsetTop ignores the tilt. */
    function markRows() {
      var tops = [];
      rvCards.forEach(function (c) { if (tops.indexOf(c.offsetTop) < 0) tops.push(c.offsetTop); });
      tops.sort(function (a, b) { return a - b; });
      rvCards.forEach(function (c) { c.classList.toggle('hfs-rv-row-b', tops.indexOf(c.offsetTop) % 2 === 1); });
    }
    var parallax = reviewsEl ? (parseFloat(reviewsEl.getAttribute('data-hfs-rv-parallax')) || 0) : 0;
    var stagger = reviewsEl ? (parseFloat(reviewsEl.getAttribute('data-hfs-rv-stagger')) || 0) : 0;
    var lastPar = null, lastCamT = '', lastExit = null;
    var lastStill = null;
    var driftCur = null, driftVel = 0, camMoving = false, frameDt = 16.7, lastTick = 0, arrivePx = 1;
    decorateReviews(); // after rvCards exists (it is read inside); styles apply in the static layout too

    /* Words are never split (no hyphens). A word longer than its card is wide makes that
       card's text shrink in 5% steps (down to 60%) until every word fits whole. */
    var TEXT = 'h1, h2, h3, h4, h5, h6, p, .h1, .h2, .h3, .h4, .h5, .h6';
    function fitWords() {
      /* The reviews heading too (outside the cards): never split, shrunk only as much as a
         word that doesn't fit whole needs. On phones the large heading type plus its panel's
         padding left less room than a long word (GONDOLTÁK) and the browser split it. */
      if (reviewsEl) {
        Array.prototype.slice.call(reviewsEl.querySelectorAll('h1, h2, h3, h4, h5, h6')).forEach(function (h) {
          if (h.closest('.rc__card')) return;
          h.style.fontSize = '';
          if (h.scrollWidth <= h.clientWidth + 1) return;
          var base = parseFloat(getComputedStyle(h).fontSize) || 16;
          for (var k = 0.95; k >= 0.5; k -= 0.05) {
            h.style.fontSize = (base * k).toFixed(1) + 'px';
            if (h.scrollWidth <= h.clientWidth + 1) break;
          }
        });
      }
      if (!gridTrack) return;
      gridTrack.querySelectorAll('[data-rc-card]').forEach(function (card) {
        var els = Array.prototype.slice.call(card.querySelectorAll(TEXT));
        els.forEach(function (el) { el.style.fontSize = ''; });
        /* Measure the text itself, not the card: corner icons hang outside the card on
           purpose and would make every icon card look too narrow. */
        var tooWide = function () { return els.some(function (el) { return el.scrollWidth > el.clientWidth + 1; }); };
        if (!tooWide()) return;
        var base = els.map(function (el) { return parseFloat(getComputedStyle(el).fontSize) || 16; });
        for (var k = 0.95; k >= 0.6; k -= 0.05) {
          els.forEach(function (el, i) { el.style.fontSize = (base[i] * k).toFixed(1) + 'px'; });
          if (!tooWide()) break;
        }
      });
    }
    /* Fonts swapping in change word widths: fit again once they have loaded. */
    if (gridTrack && document.fonts && document.fonts.ready) document.fonts.ready.then(function () { if (instances.get(root)) fitWords(); replace(); });
    if (gridTrack) {
      window.addEventListener('load', replace);
      window.addEventListener('pageshow', replace);
    }
    /* Scroll-driven CSS animations run on the browser's scrolling thread, perfectly in step
       with the page however busy the main thread is. Where supported, the pouch's drift in the
       normal-page part and the review rows' parallax are handed to the browser; the script
       only provides the distances and the stretch of scrolling (from measure()). Fast flings
       made the script-driven pouch lag, keep moving after the page stopped (spring) and catch
       up in jumps of 100+ px on phones. */
    var sdOn = !staticMode && !!(window.CSS && CSS.supports && CSS.supports('animation-timeline: scroll()') && CSS.supports('animation-range: 10px 20px'));
    if (sdOn) root.classList.add('hfs--sd');
    function curve(fn) { var pts = []; for (var i = 0; i <= 24; i++) pts.push(fn(i / 24).toFixed(4)); return 'linear(' + pts.join(', ') + ')'; }
    var glideCurve = curve(glide), outCubic = curve(function (x) { return 1 - Math.pow(1 - x, 3); }), easeIO = curve(easeInOut);
    function applyScrollDriven() {
      if (!sdOn || !vh) return;
      var start = rootTop + pinPx, unitPx = 100 * unit, pushEnd = 1 + cam.push, D, len, ease;
      if (toBottom) {
        /* At the end of the sequence the camera has no tilt (sin(pi) = 0), the full push-in and
           the full sink, so the bottom edge is fixed: the same formula render() uses. */
        var bottomEnd = boxTop + endShift * vh + boxH * (MOUTH_Y + (1 - MOUTH_Y) * pushEnd);
        D = vh * bottomAt - bottomEnd; len = arrivePx; ease = glideCurve;
      } else {
        D = driftVh * unitPx; len = driftOver * unitPx; ease = outCubic;
      }
      var s = root.style;
      s.setProperty('--hfs-dr-a', Math.round(start) + 'px');
      s.setProperty('--hfs-dr-b', Math.round(start + len) + 'px');
      s.setProperty('--hfs-dr-to', (D / pushEnd).toFixed(2) + 'px'); // inside the scaled camera
      s.setProperty('--hfs-dr-ease', ease);
      /* Everything else that follows the scroll, in page pixels (same formulas as render()):
         the tagline and hint fade at the start, the info cards' entrances, the phone card
         column, and the cards leaving after the pinned part. */
      var F = L.frames * unitPx, C = L.cards * unitPx;
      s.setProperty('--hfs-ease-io', easeIO);
      s.setProperty('--hfs-tg-a', Math.round(rootTop + 0.03 * F) + 'px');
      s.setProperty('--hfs-tg-b', Math.round(rootTop + 0.1 * F) + 'px');
      s.setProperty('--hfs-hint-a', Math.round(rootTop) + 'px');
      s.setProperty('--hfs-hint-b', Math.round(rootTop + 0.05 * F) + 'px');
      if (cardEls.length) {
        var n = cardEls.length, w = Math.min(0.3, 1 / n);
        cardEls.forEach(function (el, i) {
          var a = n > 1 ? i * (1 - w) / (n - 1) : 0;
          var fromLeft = cardsAround ? i % 2 === 0 : cardsLeft;
          el.style.setProperty('--hfs-ca', Math.round(rootTop + F + a * C) + 'px');
          el.style.setProperty('--hfs-cb', Math.round(rootTop + F + (a + w) * C) + 'px');
          el.style.setProperty('--hfs-cx', (fromLeft ? -48 : 48) + 'px');
        });
        var y0 = unitPx + 40, y1 = Math.min(y0, unitPx * 0.85 - (cardsH || 0));
        s.setProperty('--hfs-col-a', Math.round(rootTop + F) + 'px');
        s.setProperty('--hfs-col-b', Math.round(rootTop + F + C) + 'px');
        s.setProperty('--hfs-col-y0', Math.round(y0) + 'px');
        s.setProperty('--hfs-col-y1', Math.round(y1) + 'px');
        /* Phones: the column keeps scrolling up at page speed until it has left the screen,
           without fading (cards were fading while still being read, and the reviews came up
           over them). Desktop: up at page speed while fading over the Exit distance. */
        var phoneNow = window.innerWidth < 750;
        var ex = phoneNow ? unitPx + (cardsH || 0) : (exitVh > 0 ? exitVh * unitPx : 1);
        s.setProperty('--hfs-ex-a', Math.round(start) + 'px');
        s.setProperty('--hfs-ex-b', Math.round(start + ex) + 'px');
        s.setProperty('--hfs-ex-to', (phoneNow || exitVh > 0 ? -Math.round(ex) : 0) + 'px');
        s.setProperty('--hfs-ex-op', phoneNow ? '1' : '0');
      }
      if (parallax && gridTrack) {
        var flow = Math.max(1, rootH - window.innerHeight - pinPx);
        s.setProperty('--hfs-par-a', Math.round(start) + 'px');
        s.setProperty('--hfs-par-b', Math.round(start + flow) + 'px');
        s.setProperty('--hfs-par-amp', parallax + 'px');
      }
    }
    function measure() {
      placeColumn();
      if (cardsEl) { // phones: the column's real height (the layer's own box is shorter: the cards overflow it)
        var lastCard = cardsEl.lastElementChild;
        cardsH = lastCard ? lastCard.offsetTop + lastCard.offsetHeight : cardsEl.offsetHeight; // layout: transforms ignored
      }
      dirty = false;
      vw = window.innerWidth;
      lengths();
      unit = (probe.offsetHeight || window.innerHeight) / 100; // px per CSS vh
      /* Touch phones: one fixed screen height for the hero, in the script and in the CSS
         (--hfs-u), so browser bars sliding in and out (toolbars, Chrome's translation bar,
         in-app browsers) change nothing mid-scroll. It is the largest height seen at this width:
         it only grows (never a white strip at the bottom), and a new width starts afresh. */
      if (touchPhone()) {
        var uw = document.documentElement.clientWidth, seen = Math.max(probe.offsetHeight || 0, window.innerHeight);
        if (heroW !== uw) { heroW = uw; heroH = seen; heroSmall = 0; }
        else if (seen > heroH + 1) heroH = seen;
        unit = heroH / 100;
        root.style.setProperty('--hfs-u', (heroH / 100).toFixed(3) + 'px');
      } else if (heroW) { heroW = 0; root.style.removeProperty('--hfs-u'); }
      /* The pouch is sized from the stable screen height (CSS vh), not innerHeight: on
         phones innerHeight changes whenever the address bar slides, and each change
         used to resize and fully redraw the canvas mid-scroll (a visible stutter). */
      vh = unit * 100;
      /* "With the page": the cards stretch lasts exactly as long as the column takes to rise
         from just below the screen (+40 px) until its last card's bottom is at 85% of it, the
         same end point the timed column has. */
      if (colPageNow) {
        L.cards = (0.15 * vh + 40 + cardsH) / vh;
        root.style.setProperty('--hfs-pin', (L.frames + L.cards) * 100);
      }
      placeGrid();
      pinPx = (L.frames + L.cards) * 100 * unit; // matches the spacer's CSS height exactly
      geoDirty = true; // the spacer (and so the hero's height) may have changed
      arrivePx = Math.max(1, (flow ? parseFloat(getComputedStyle(flow).paddingTop) || 0 : 0) + 0.35 * unit * 100);
      geoDirty = true; // applyScrollDriven() runs with the fresh page position
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      var frameH = window.innerWidth < 750 ? frameHM : frameHD; // same breakpoint as the CSS
      endShift = window.innerWidth < 750 ? endShiftM : endShiftD;
      boxH = vh * frameH;
      boxW = boxH * ar;
      /* Phones: the opening at "Pouch opening height (phone)" of the visible screen when the page
         opens. The hero starts below the header then, so its own top is subtracted. */
      var phoneNow = window.innerWidth < 750, mouthPx;
      if (phoneNow) {
        var heroTop = Math.max(0, Math.min(layoutTop(root), vh * 0.25));
        mouthPx = smallScreenH() * mouthPhone - heroTop;
      } else mouthPx = vh * mouthScreen;
      boxTop = mouthPx - boxH * MOUTH_Y;
      var baseTop = boxTop; // where the CSS already puts the box
      /* The tagline must never cover the pouch. If it doesn't fit between the top of
         the screen and the opening: first lower the whole frame (but keep the
         opening above 90% of the screen); if that's still not enough, scale the
         tagline down to fit. offsetHeight ignores transforms, so tagH is always the
         tagline's natural height. */
      fitTagWords();
      tagFit = 1;
      if (anchored && !staticMode && tagEls.length) {
        var topRoom = Math.max(12, vh * 0.04), gapO = Math.max(16, vh * 0.025), tagH = 0;
        for (var q = 0; q < tagEls.length; q++) tagH += tagEls[q].offsetHeight;
        var openTop = boxTop + boxH * OPENING_TOP_Y;
        var short = topRoom + gapO + tagH - openTop;
        if (short > 0 && !phoneNow) { // phones keep the opening where it was set: the tagline shrinks instead (below)
          var shift = Math.max(0, Math.min(short, vh * 0.9 - openTop));
          boxTop += shift;
          openTop += shift;
        }
        var avail = openTop - gapO - topRoom;
        if (tagH > avail && tagH > 0) tagFit = Math.max(0.25, avail / tagH);
      }
      for (var z = 0; z < tagEls.length; z++) tagEls[z].style.scale = tagFit === 1 ? '' : String(tagFit);
      camera.style.width = boxW + 'px';
      camera.style.height = boxH + 'px';
      camera.style.marginLeft = -boxW / 2 + 'px';
      /* Lowering the box so the tagline fits is a translate, not a new top: the tagline's height
         changes when the web fonts arrive, and moving the huge canvas by its top counted as a
         layout shift (Lighthouse CLS 0.626 on the homepage). Visually identical. */
      camera.style.top = baseTop + 'px';
      camera.style.translate = boxTop !== baseTop ? '0 ' + (boxTop - baseTop).toFixed(1) + 'px' : '';
      camera.style.transformOrigin = '50% ' + MOUTH_Y * 100 + '%'; // push-in around the mouth
      /* Tagline above the opening: the overlay is full-stage, so its bottom padding
         puts the text's bottom edge just above the mouth. */
      if (anchored && !staticMode) {
        /* Bottom edge of the tagline just above the opening. Fitting (above) already
           guarantees the whole tagline is on screen. */
        /* From the overlay's real height (inset 0 in the stage: the stage's height), not vh: the
           stage is 100lvh, and in in-app browsers (the Google app, ...) 100vh and 100lvh can
           differ by their own bars, which put the tagline that much lower, behind the opening. */
        var stageH = tag.offsetHeight || vh;
        var pb = Math.max(0, stageH - (boxTop + boxH * OPENING_TOP_Y - Math.max(16, vh * 0.025)));
        tag.style.paddingBottom = pb + 'px';
      }
      /* Pixel budget: the canvas is taller than the screen (most of it off-screen),
         so full retina resolution costs a lot for nothing. Cap the backing store;
         every draw (and every motion-blur sample) scales with this. */
      var budget = vw < 768 ? MAX_PX_PHONE : MAX_PX;
      px = Math.max(Math.min(1, dpr), Math.min(dpr, Math.sqrt(budget / Math.max(1, boxW * boxH)))); // never softer than the screen itself
      var cw = Math.round(boxW * px), ch = Math.round(boxH * px);
      if (cw > 0 && ch > 0 && (canvas.width !== cw || canvas.height !== ch)) {
        canvas.width = cw;
        canvas.height = ch;
        drawnKey = ''; // resizing clears the canvas
      }
    }

    function sized(u) {
      var need = boxW * px, cap = vw < 768 ? MAX_W_PHONE : MAX_W, w = WIDTH_STEPS[0];
      for (var k = 0; k < WIDTH_STEPS.length && WIDTH_STEPS[k] <= cap; k++) {
        w = WIDTH_STEPS[k];
        if (w >= need) break;
      }
      try {
        var url = new URL(u, window.location.href);
        url.searchParams.set('width', w);
        return url.toString();
      } catch (e) {
        return u;
      }
    }

    /* Frames are kept as ImageBitmaps: decoded once and kept ready. An <img> may have its decoded
       pixels dropped by the browser and decoded again when drawn: a whole JPEG decode inside a
       scroll frame (the hitches when the pouch starts moving). The <img> is released after. */
    function ready(i, img, release) {
      if (!window.createImageBitmap) return;
      createImageBitmap(img).then(function (bmp) {
        if (!instances.get(root) && started) { bmp.close && bmp.close(); return; } // torn down meanwhile
        frames[i] = bmp;
        drawnKey = '';
        if (release) img.src = '';
        if (typeof kick === 'function') kick();
      }).catch(function () {}); // keep the <img>
    }
    function load(i, cb) {
      /* Frame 1 is the poster the HTML already loads (fetched early, at high priority): use that
         very image instead of downloading the first frame a second time. */
      if (i === 0 && posterIsFrame1) {
        var use = function () {
          if (!poster.naturalWidth) return;
          frames[0] = poster;
          drawnKey = '';
          if (typeof kick === 'function') kick();
          ready(0, poster, false); // then swap in a bitmap: kept decoded (the poster stays on the page)
        };
        if (poster.complete) use(); else poster.addEventListener('load', use, { once: true });
        return;
      }
      var finished = false;
      var done = function () { if (!finished) { finished = true; if (cb) cb(); } };
      /* Download the file and decode it straight into a bitmap: createImageBitmap(blob) decodes
         off the main thread. Making the bitmap from an <img> copied every frame on the main thread
         (about 750 ms of a slow phone's load, just when the visitor starts to scroll). Older
         browsers, or a failed fetch, take the <img> route below. */
      if (window.fetch && window.createImageBitmap) {
        fetch(sized(urls[i]), { credentials: 'same-origin', priority: 'low' }) // low, like the <img> it replaces
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.blob(); })
          .then(function (b) { return createImageBitmap(b); })
          .then(function (bmp) {
            if (!instances.get(root) && started) { bmp.close && bmp.close(); return done(); } // torn down meanwhile
            frames[i] = bmp;
            drawnKey = ''; // a frame arrived: redraw in case it's the one we need
            if (typeof kick === 'function') kick();
            done();
          })
          .catch(function () { viaImg(); });
        return;
      }
      viaImg();
      function viaImg() {
      var img = new Image();
      img.decoding = 'async';
      img.onerror = done;
      img.onload = function () {
        (img.decode ? img.decode() : Promise.resolve()).catch(function () {}).then(function () {
          frames[i] = img;
          drawnKey = ''; // a frame arrived: redraw in case it's the one we need
          if (typeof kick === 'function') kick();
          ready(i, img, true);
          done();
        });
      };
      img.src = sized(urls[i]);
      }
    }

    /* Nearest loaded frame at or before i (falls forward if none before). */
    function pick(i) {
      for (var k = i; k >= 0; k--) if (frames[k]) return k;
      for (k = i + 1; k < frames.length; k++) if (frames[k]) return k;
      return -1;
    }

    /* Draws a weighted mix of frames ({index: weight}, weights summing to 1).
       Additive ('lighter') compositing of weighted frames onto a cleared canvas is
       an exact per-pixel average, so the pouch stays fully opaque mid-mix instead
       of dipping to see-through. One frame = a crisp image; two = a cross-fade;
       several = motion blur. */
    function drawMix(w) {
      var idx = [], key = '', sum = 0;
      /* In motion-blur mixes (3+ frames), samples under 6% are invisible but cost a
         full draw each: drop them and re-weight the rest so the mix still sums to 1.
         Plain cross-fades keep every share, so their edges never pop. */
      var many = Object.keys(w).length > 2, min = many ? 0.06 : 0.004;
      for (var k in w) if (w[k] > min) { idx.push(+k); sum += w[k]; }
      if (!idx.length) return;
      if (sum > 0 && sum < 0.999) for (var r = 0; r < idx.length; r++) w[idx[r]] /= sum;
      idx.sort(function (x, y) { return x - y; });
      for (var j = 0; j < idx.length; j++) key += idx[j] + ':' + Math.round(w[idx[j]] * 200) + ';';
      if (key === drawnKey) return;
      drawnKey = key;
      var t0 = performance.now();
      ctx.globalCompositeOperation = 'source-over';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'lighter';
      for (j = 0; j < idx.length; j++) {
        ctx.globalAlpha = w[idx[j]];
        /* Leave out each frame's outermost pixels: upscaled frames carry faint half-transparent
           edge pixels that showed as two thin vertical lines beside the tagline and pouch. */
        var im = frames[idx[j]];
        if (im instanceof HTMLImageElement) {
          /* An <img> from srcset (the poster) reports its size corrected for screen density, not
             in pixels, so cutting a source area by numbers drew part of it (black edges, or a
             zoomed crop) as the animation started. Draw the whole image a little larger than the
             canvas instead: the same edge margin falls outside, whatever size it reports. */
          var mx = Math.max(3, Math.round(im.naturalWidth / 300)) / im.naturalWidth, my = Math.max(3, Math.round(im.naturalHeight / 300)) / im.naturalHeight;
          var dw = canvas.width / (1 - 2 * mx), dh = canvas.height / (1 - 2 * my);
          ctx.drawImage(im, -dw * mx, -dh * my, dw, dh);
        } else { // ImageBitmap: its size is its real pixel size
          var iw = im.width, ih = im.height, e = Math.max(3, Math.round(iw / 300)); // +1: scaling blends the next pixel in
          ctx.drawImage(im, e, e, iw - 2 * e, ih - 2 * e, 0, 0, canvas.width, canvas.height);
        }
      }
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      /* Safety valve: if blended draws are slow on this device, halve the motion
         blur (repeatedly if needed). Fast machines keep it; slow ones stay fluid. */
      if (idx.length > 1) {
        drawMs = drawMs * 0.85 + (performance.now() - t0) * 0.15;
        if (drawMs > 12 && blurGain > 0.3 && performance.now() - lastCut > 1000) {
          blurGain *= 0.5;
          lastCut = performance.now();
        }
      }
      if (poster && poster.style.visibility !== 'hidden') poster.style.visibility = 'hidden';
    }

    /* Adds `amount` of weight at fractional position s, split between the two
       neighbouring loaded frames. */
    function addWeight(w, s, amount) {
      var i = Math.floor(s), f = s - i;
      var a = pick(i), b = f > 0.001 ? pick(i + 1) : -1;
      if (a < 0) return;
      if (b < 0 || b === a) { w[a] = (w[a] || 0) + amount; return; }
      w[a] = (w[a] || 0) + amount * (1 - f);
      w[b] = (w[b] || 0) + amount * f;
    }

    /* A frame position, optionally smeared back along the direction of travel by
       `span` frames (motion blur). */
    function draw(pos, span, dir) {
      var w = {};
      if (!(span > 0.12)) addWeight(w, pos, 1);
      else {
        var n = Math.min(MAX_SAMPLES, Math.ceil(span * 2) + 1);
        /* Later samples (closer to the current frame) weigh more, so the image
           stays readable and the trail fades out behind it. */
        var tot = 0, ws = [];
        for (var j = 0; j < n; j++) { ws.push(j + 1); tot += j + 1; }
        for (j = 0; j < n; j++) {
          var sp = pos - dir * span * (1 - j / (n - 1));
          addWeight(w, Math.max(0, Math.min(frames.length - 1, sp)), ws[j] / tot);
        }
      }
      drawMix(w);
    }

    /* The page (or the theme editor's preview frame) can be laid out at zero
       height before it's sized. Re-measure whenever the viewport changes, and
       never divide by a zero-length scroll range. */
    /* Per frame only the scroll offset is read (no layout). The hero's page position and
       height are measured when they can change (resize, content above or inside resizing):
       reading them every frame forced a full layout on each frame while the page was still
       loading (about 30 ms of blocking time on a phone). */
    var scroller = null, rootTop = 0, rootH = 0, geoDirty = true;
    /* The hero's position in the scrolling page, from the layout (offsets). "Position on screen
       + scroll position" can be out of step for a moment on iPhones mid-scroll (sliding
       toolbar, momentum), which put the hero's ranges and the pouch a few px off and back. */
    function layoutTop(el) {
      if (scroller) return el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop; // inner scroller: its own coordinates
      var y = 0;
      for (var n = el; n; n = n.offsetParent) y += n.offsetTop;
      return y;
    }
    function scrollPos() { return scroller ? scroller.scrollTop : (window.pageYOffset || document.documentElement.scrollTop || 0); }
    function measureGeo() {
      geoDirty = false;
      rootTop = layoutTop(root);
      rootH = root.offsetHeight;
      if (colPageNow && colHost) {
        var colAt = Math.round(rootTop + L.frames * 100 * unit + 100 * unit + 40 - layoutTop(flow));
        if (colHost._hfsTop !== colAt) { colHost.style.top = colAt + 'px'; colHost._hfsTop = colAt; }
      }
      /* Phones: the card column and the reviews both move at page speed after the pinned
         part, so the gap between them never changes. Without the fade, the reviews would sit
         over the last cards; give the reviews' top space exactly what's needed for the column
         to end above them (its bottom at 85% of the screen), plus a gap. */
      if (flow && cardEls.length) {
        var cur = parseFloat(root.style.getPropertyValue('--hfs-col-push')) || 0, push = 0;
        if (window.innerWidth < 750 && !staticMode) {
          var scr = 100 * unit, colBottom = Math.min(scr + 40 + cardsH, scr * 0.85);
          var padT = (parseFloat(getComputedStyle(flow).paddingTop) || 0) - cur;
          var contentAtPinEnd = layoutTop(flow) - (rootTop + pinPx) + padT;
          push = Math.max(0, Math.round(colBottom + 48 - contentAtPinEnd));
        }
        if (push !== cur) {
          root.style.setProperty('--hfs-col-push', push + 'px');
          rootH = root.offsetHeight;
          dirty = true; // re-measure once: the pouch's arrival is timed to the reviews' top space
        }
      }
      applyScrollDriven();
    }
    function range() { return Math.max(0, rootH - window.innerHeight); }
    /* target: pixels scrolled into the hero (0 at its top). */
    var dirty = false;
    function readTarget() {
      if (dirty || window.innerWidth !== vw) measure();
      else if (gridTrack && document.documentElement.clientWidth !== placedCw) placeGrid(); // a scrollbar came or went
      if (geoDirty) measureGeo();
      var r = range();
      flowPx = Math.max(1, r - pinPx);
      target = Math.min(r, Math.max(0, scrollPos() - rootTop));
    }

    /* Soft start, steady middle, soft stop (a trapezoid speed profile). Unlike an ease-in-out
       curve, whose peak speed is 1.5-3x the average, the peak here is only 1.33x, so the pouch
       never outruns the page in the middle of the move. */
    function glide(t) {
      var A = 0.25, v = 1 / (1 - A);
      if (t <= 0) return 0;
      if (t >= 1) return 1;
      if (t < A) return 0.5 * v * t * t / A;
      if (t > 1 - A) return 1 - 0.5 * v * (1 - t) * (1 - t) / A;
      return v * (A / 2 + t - A);
    }

    function phase(at, len) { return len > 0 ? clamp(at / len) : 0; }

    function render(sm, raw) {
      /* sm: smoothed pixels scrolled (drives the frames and cards); raw: the actual
         scroll, for everything that must move exactly with the page. p: progress
         within the frames phase, which is what the original timeline below runs on. */
      renderedAt = raw;
      var sd = Math.min(sm, pinPx) / (100 * unit); // screen heights scrolled within the pinned part
      var p = clamp(sd / L.frames);
      /* Raw progress for everything that moves with the page (tagline, hint, cards): only the
         pouch's frames and camera follow the smoothed scroll. Smoothed, the cards trailed a fast
         scroll by up to ~330 px in browsers without scroll-driven animations (Firefox, Safari
         before 26), and kept moving after it stopped. */
      var sdR = Math.min(raw, pinPx) / (100 * unit), pR = clamp(sdR / L.frames);
      var cp = phase(sdR - L.frames, L.cards);
      var beyond = Math.max(0, raw - pinPx); // pixels into the normal-page part
      var still = beyond > 0;
      if (still !== lastStill) { root.classList.toggle('hfs--still', still); lastStill = still; } // idle float fades out
      if (parallax && gridTrack && !staticMode) {
        var par = Math.round((seg(beyond, 0, flowPx) - 0.5) * parallax);
        if (sdOn) par = 0; // browser-driven (CSS scroll timeline on the card wrappers)
        /* Written on each card, not the track: an inherited variable on the track made every
           element inside all the review cards restyle on every scroll frame (registered as
           non-inheriting in the CSS, so only the cards themselves update). */
        if (par !== lastPar) { rvCards.forEach(function (c) { c.style.setProperty('--hfs-par', par + 'px'); }); lastPar = par; }
      }

      if (tagEls.length) {
        var t = easeInOut(seg(pR, 0.03, 0.1));
        var tr0 = easeInOut(seg(target / (100 * unit), 0.03 * L.frames, 0.1 * L.frames)); // raw scroll, like the CSS
        tagEls.forEach(function (c) {
          if (!sdOn) {
            c.style.opacity = 1 - t;
            c.style.transform = 'translateY(' + -t * 40 + 'px)';
          }
          var pe = (sdOn ? tr0 : t) > 0.5 ? 'none' : '';
          if (c.style.pointerEvents !== pe) c.style.pointerEvents = pe; // a faded tagline must not block clicks below it
        });
      }
      if (hint && !sdOn) hint.style.opacity = 1 - seg(pR, 0, 0.05);

      /* Info cards: each slides in on its own stretch of the phase. Desktop: they stack
         up beside the pouch. Phones: each one replaces the previous. They leave when
         the reviews arrive. */
      if (cardEls.length) {
        var n = cardEls.length, phone = vw < 750;
        var ins = cardEls.map(function (el, i) {
          /* Entrances fill the whole phase, so the last card lands exactly as the page
             starts scrolling on: no stretch of scrolling where nothing moves.
             Desktop: overlapping slide-ins. Phones: one card at a time (each fades in
             over 35% of its turn and hands over to the next). */
          var w = phone ? 0.35 / (n - 0.65) : Math.min(0.3, 1 / n);
          var a = n > 1 ? i * (1 - w) / (n - 1) : 0;
          if (phone) a = i / (n - 0.65);
          return easeInOut(seg(cp, a, Math.min(1, a + w)));
        });
        cardEls.forEach(function (el, i) {
          var e = ins[i], o, tr;
          if (phone && colPageNow) {
            o = 1; tr = ''; // scrolls with the page by itself (normal-page part)
          } else if (phone) {
            /* The column moves up with the scroll: it starts just below the screen (the
               first card comes in from the bottom as the cards phase begins) and ends with
               the last card's bottom at 85%, so the next card is always peeking in while
               reading. One offset for the whole column; its height is measured on resize. */
            var scr = unit * 100, y0 = scr + 40, y1 = Math.min(y0, scr * 0.85 - (cardsH || 0)); // +40: fully below the screen until the cards phase
            o = 1;
            /* Straight up, on the card's wrapper (as the scroll-driven version does): a translate
               in the card's own transform runs along its tilted axis, so the column drifted
               sideways by up to ~24 px in browsers without scroll-driven animations. */
            var yCol = Math.round(y0 + (y1 - y0) * cp), wrap = el.parentElement !== cardsEl ? el.parentElement : null;
            if (wrap) { tr = ''; if (!sdOn && wrap._hfsY !== yCol) { wrap.style.translate = '0 ' + yCol + 'px'; wrap._hfsY = yCol; } }
            else tr = 'translateY(' + yCol + 'px)';
          } else {
            o = e;
            /* Each card enters from its own side: around the pouch, odd cards from
               the left and even ones from the right. */
            var fromLeft = cardsAround ? i % 2 === 0 : cardsLeft;
            tr = 'translateX(' + (1 - e) * (fromLeft ? -48 : 48) + 'px)';
          }
          if (sdOn) { // the browser animates opacity and position; only the class follows here
            var live = o > 0.5;
            if (el._hfsLive !== live) { el.classList.toggle('is-live', live); el._hfsLive = live; }
            return;
          }
          var k2 = o.toFixed(3) + tr;
          if (el._hfsLast === k2) return; // unchanged: no style write, no restyle
          el._hfsLast = k2;
          el.style.opacity = o;
          el.style.transform = tr;
          el.classList.toggle('is-live', o > 0.5);
        });
        /* After the pinned part the cards scroll away exactly like page content
           (translate, so the layout transform stays untouched) and fade out. */
        var gone = exitVh > 0 ? seg(beyond, 0, exitVh * 100 * unit) : (beyond > 0 ? 1 : 0);
        /* Once fully faded they stop moving: no style writes on every later scroll frame. */
        var exitKey = gone >= 1 ? 'gone' : beyond > 0 ? Math.round(beyond) + '|' + gone.toFixed(3) : '';
        if (exitKey !== lastExit) {
          lastExit = exitKey;
          if (!sdOn) {
            if (phone && colPageNow) { /* scrolls away with the page by itself */ }
            else if (phone) { cardsEl.style.translate = beyond > 0 ? '0 ' + (-Math.round(Math.min(beyond, 100 * unit + cardsH))) + 'px' : ''; cardsEl.style.opacity = 1; } // phones: scroll away, no fade
            else {
              if (gone < 1) cardsEl.style.translate = beyond > 0 ? '0 ' + (-Math.round(beyond)) + 'px' : '';
              cardsEl.style.opacity = 1 - gone;
            }
          }
          /* Faded-out cards must not catch clicks meant for the reviews scrolling over. */
          cardsEl.classList.toggle('is-gone', gone > 0.5);
        }
      }
      /* Camera move over the frame sequence: push-in toward the mouth, a slow
         sideways sway and a slight tilt. Amounts come from the Camera setting. */
      var k = seg(p, FRAMES_FROM, 1);
      var push = 1 + cam.push * easeInOut(seg(p, FRAMES_FROM, 0.85));
      var dx = Math.sin(k * Math.PI * 1.5) * cam.sway * vw;
      var rot = Math.sin(k * Math.PI) * cam.tilt;
      /* End framing: in the second half of the sequence the pouch sinks, so the
         spill above the opening stays on screen instead of leaving the empty pouch. */
      var sink = endShift * vh * easeInOut(seg(p, 0.45, 0.95));
      /* Normal-page part: the pouch drifts a little and eases to a stop. It follows the
         smoothed scroll (sm), like the frames, so it glides instead of moving rigidly. */
      var unitPx = 100 * unit, drift;
      if (toBottom) {
        /* Ease from the end framing to the pouch's bottom at the chosen screen height,
           arriving exactly as the hero ends. The push-in scales around the mouth, so the
           bottom edge sits at boxTop + boxH * (MOUTH_Y + (1 - MOUTH_Y) * push). */
        /* Arrives as the reviews finish fading in: by the time their top has risen to
           65% of the screen (space before the reviews + 35% of a screen). */
        var bottomNow = boxTop + sink + boxH * (MOUTH_Y + (1 - MOUTH_Y) * push);
        drift = (vh * bottomAt - bottomNow) * glide(seg(beyond, 0, arrivePx));
      } else {
        var t = seg(beyond, 0, driftOver * unitPx);
        drift = driftVh * unitPx * (1 - Math.pow(1 - t, 3));
      }
      /* The pouch follows that position on a critically damped spring: a mouse wheel scrolls
         in 100 px jumps, and catching up with each jump made the pouch move in bursts. A
         spring changes speed continuously (no jolt at each notch) and never overshoots.
         Time-based, so it behaves the same at 60 and 144 Hz. */
      /* Browser-driven where supported (a CSS scroll-timeline animation on the canvas, see
         applyScrollDriven); otherwise tied directly to the scroll. The former spring lagged
         behind fast flings and kept the pouch moving after the page had stopped. */
      if (sdOn) drift = 0;
      driftCur = drift; driftVel = 0;
      camMoving = false;
      var camT = 'translate(' + dx.toFixed(2) + 'px,' + (sink + drift).toFixed(2) + 'px) rotate(' + rot.toFixed(3) + 'deg) scale(' + push.toFixed(4) + ')';
      if (camT !== lastCamT) { camera.style.transform = camT; lastCamT = camT; } // no write when unchanged

      var pos = seg(p, FRAMES_FROM, 1) * (frames.length - 1);
      var i = Math.floor(pos), f = pos - i;
      var fb = seg(f, 0.5 - BLEND / 2, 0.5 + BLEND / 2);
      var shown = Math.min(i + fb * fb * (3 - 2 * fb), frames.length - 1);
      /* Motion blur: how far the sequence moved since the last screen refresh,
         smoothed, sets the trail length. Later frames (the spill) blur more. */
      velS = velS * 0.6 + (lastPos < 0 ? 0 : pos - lastPos) * 0.4;
      lastPos = pos;
      var spill = 0.5 + pos / Math.max(1, frames.length - 1);
      var smear = Math.min(maxSpan, Math.abs(velS) * blurGain * spill);
      draw(shown, smear, velS >= 0 ? 1 : -1);
    }

    /* Reads the scroll position every frame rather than relying on scroll events,
       so it works whatever element is doing the scrolling. Progress chases the
       position instead of jumping to it, which is what makes wheel and trackpad
       scrolling read as continuous. Drawing only happens when something changed. */
    function tick(now) {
      now = now || performance.now();
      frameDt = lastTick ? Math.min(64, Math.max(1, now - lastTick)) : 16.7;
      lastTick = now;
      readTarget();
      if (!isFinite(cur)) cur = target; // never let a bad frame stick
      var diff = target - cur;
      /* CHASE is the share covered per 60 Hz frame; scaled by real frame time so the scrub
         feels the same on 60, 120 and 144 Hz screens. */
      if (diff !== 0) cur = Math.abs(diff) < 0.5 ? target : cur + diff * (1 - Math.pow(1 - CHASE, frameDt / 16.7));
      var blurring = Math.abs(velS) > 0.002;
      if (diff !== 0 || drawnKey === '' || blurring || camMoving || renderedAt !== target) render(cur, target);
      /* Keep looping only while something is still moving (including a motion-blur
         trail settling back to a crisp frame). Once settled the loop stops;
         scrolling, resizing or a newly loaded frame starts it again. */
      /* A missing drawing only keeps the loop alive if there's a frame to draw: while the
         first frame is still downloading, spinning every frame would only force layouts
         (each frame that arrives restarts the loop itself). */
      if (cur !== target || blurring || camMoving || (drawnKey === '' && pick(0) >= 0)) raf = requestAnimationFrame(tick);
      else { raf = 0; lastTick = 0; }
    }
    function kick() { if (!raf && !staticMode) raf = requestAnimationFrame(tick); }

    measure();
    root.classList.add('hfs--ready'); // the box is sized and placed: show it

    if (staticMode) {
      /* Reduced motion: no pinning, no scrubbing — just the final frame. */
      root.classList.add('hfs--static');
      root.classList.add('hfs--ready');
      var last = urls.length - 1;
      load(last);
      (function waitLast() {
        if (window.innerWidth !== vw) measure();
        if (frames[last]) { drawnKey = ''; draw(last); return; }
        raf = requestAnimationFrame(waitLast);
      })();
      instances.set(root, { destroy: function () { cancelAnimationFrame(raf); } });
      return;
    }

    /* The poster stands in for frame 1 only if it is the same file (the editor setting could
       point at another image); otherwise frame 1 downloads normally and the poster stays hidden. */
    function fileOf(u) { try { return new URL(u, location.href).pathname; } catch (e) { return ''; } }
    var posterIsFrame1 = !!(poster && poster.getAttribute('src') && urls.length &&
      fileOf(poster.currentSrc || poster.getAttribute('src')) === fileOf(urls[0]));
    if (poster && poster.getAttribute('src') && !posterIsFrame1) poster.style.visibility = 'hidden';
    if (poster && !poster.getAttribute('src')) { // no First frame setting: no poster in the HTML
      poster.alt = frameEls[0].getAttribute('data-alt') || '';
      poster.src = sized(urls[0]);
      poster.hidden = false;
      posterIsFrame1 = true;
    }
    /* The pouch's idle float animation pauses while the hero is off screen. */
    var awayIo = 'IntersectionObserver' in window ? new IntersectionObserver(function (es) {
      root.classList.toggle('hfs--away', !es[0].isIntersecting);
    }) : null;
    if (awayIo) awayIo.observe(root);
    var flowIo = null;
    if (flow && 'IntersectionObserver' in window) {
      root.classList.add('hfs--flow-anim');
      flowIo = new IntersectionObserver(function (es) {
        var n = 0;
        es.forEach(function (e) {
          if (!e.isIntersecting) return;
          var el = e.target;
          flowIo.unobserve(el);
          if (el.hasAttribute('data-rc-card')) {
            /* Cards land one by one: the stagger counts within this batch of arrivals. */
            var d = n++ * stagger;
            el.style.setProperty('--hfs-d', d + 'ms');
            setTimeout(function () { el.style.removeProperty('--hfs-d'); el.classList.add('hfs-landed'); }, d + 1300);
          }
          el.classList.add('is-in');
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
      [reviewsEl, ctaEl].forEach(function (el) { if (el && flow.contains(el)) flowIo.observe(el); });
      if (reviewsEl && flow.contains(reviewsEl)) rvCards.forEach(function (c) { flowIo.observe(c); });
    }
    load(0); // the poster covers the wait for this one
    readTarget();
    cur = target;
    render(cur, target);
    raf = requestAnimationFrame(function () {
      if (!started) {
        started = true; // after first paint: fetch the rest, coarse to fine
        /* Not in order: the last frame, then the middle, then the quarters, and so on. In order,
           someone scrolling soon after opening ran ahead of the downloads: the animation stalled
           on the last frame that had arrived, then jumped several frames. This way the whole
           animation is covered early (coarsely), and the gaps fill in. Same total download. */
        var order = [], seen = { 0: true }, n = urls.length;
        var add = function (k) { if (k > 0 && k < n && !seen[k]) { seen[k] = true; order.push(k); } };
        add(n - 1);
        for (var step = (n - 1) / 2; step >= 1; step /= 2) for (var x = step; x < n - 1; x += step * 2) add(Math.round(x));
        for (var r = 1; r < n; r++) add(r); // anything the halving missed
        /* Three at a time: requested all at once they share the bandwidth and all arrive late
           together; this way the key frames really arrive first. */
        var inflight = 0;
        var pump = function () {
          while (inflight < 3 && order.length) {
            inflight++;
            load(order.shift(), function () { inflight--; pump(); });
          }
        };
        pump();
      }
      tick();
    });
    /* Capture phase: catches scrolling on whichever element scrolls (Savor can make
       it the body rather than the window). Passive, so it never blocks scrolling. */
    var opts = { capture: true, passive: true };
    var ro = null;
    if (window.ResizeObserver && tagEls.length && !staticMode) {
      ro = new ResizeObserver(function () { measure(); drawnKey = ''; kick(); });
      tagEls.forEach(function (c) { ro.observe(c); });
    }
    /* On touch phones the browser's toolbar slides in and out (newer Safari minimises it on
       every small change of scroll direction), firing a resize each time that changes only the
       height. Where the CSS screen-height units stay put (Safari), the hero's geometry doesn't
       change, and re-measuring mid-scroll re-placed the pinned pouch (it jumped behind the
       cards): skip those. See onResize for in-app browsers, where the units do change. */
    var onTouch = touchPhone();
    var lastResizeW = document.documentElement.clientWidth;
    function onResize() {
      var w = document.documentElement.clientWidth;
      /* Skip only if the CSS screen-height unit really stayed the same (Safari's own toolbar).
         In-app browsers (the Google app, ...) resize their page view instead, and there 100vh
         changes too: the stage and the tagline's space follow it, so the hero must re-measure
         or the opening and the tagline get out of step (the tagline ended up behind it). */
      if (onTouch && w === lastResizeW) {
        // Bars only: the hero keeps its fixed height. Grow it once if the screen is now taller.
        var seen = Math.max(probe ? probe.offsetHeight : 0, window.innerHeight);
        if (seen <= heroH + 1) { kick(); return; }
      }
      lastResizeW = w;
      dirty = true; geoDirty = true; kick();
    }
    /* Savor may scroll an element (e.g. .page-wrapper) instead of the window: use whichever
       element containing the hero actually scrolled. */
    function onScroll(e) {
      var t = e.target;
      if (t && t.nodeType === 1 && t !== document.documentElement && t !== document.body && t.contains(root) && scroller !== t) {
        scroller = t; geoDirty = true;
      }
      kick();
    }
    /* Content above the hero (announcement bar, fonts, images) or inside it (reviews,
       images) changing size moves or resizes the hero: re-measure then. */
    var geoRo = window.ResizeObserver ? new ResizeObserver(function () { geoDirty = true; kick(); }) : null;
    if (geoRo) { geoRo.observe(document.body); geoRo.observe(root); }
    document.addEventListener('scroll', onScroll, opts);
    window.addEventListener('resize', onResize, opts);

    instances.set(root, {
      destroy: function () {
        cancelAnimationFrame(raf);
        raf = 0;
        document.removeEventListener('scroll', onScroll, opts);
        if (geoRo) geoRo.disconnect();
        if (ro) ro.disconnect();
        if (flowIo) flowIo.disconnect();
        if (awayIo) awayIo.disconnect();
        if (gridTrack) {
          gridTrack.style.width = ''; gridTrack.style.maxWidth = ''; gridTrack.style.marginLeft = '';
          gridTrack.querySelectorAll(TEXT).forEach(function (el) { el.style.fontSize = ''; });
          gridTrack.style.removeProperty('--hfs-par');
          rvCards.forEach(function (c) { c.style.removeProperty('--hfs-par'); });
        }
        window.removeEventListener('resize', onResize, opts);
        window.removeEventListener('load', replace);
        window.removeEventListener('pageshow', replace);
      }
    });
  }

  function unmount(root) {
    var inst = instances.get(root);
    if (!inst) return;
    inst.destroy();
    instances.delete(root);
  }

  function mountAll(scope) {
    (scope || document).querySelectorAll('[data-hfs]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mountAll();
    });
  } else {
    mountAll();
  }

  /* The theme editor re-renders the section on every setting or block change.
     Without these the section either dies or leaks an animation loop per edit. */
  /* Theme editor: selecting a block that only shows deep into the scroll (an info
     card, the reviews, the closing button) scrolls the page to where it's visible. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', function (e) {
      var el = e.target, root = el && el.closest && el.closest('[data-hfs]');
      if (!root || root.classList.contains('hfs--static')) return;
      /* Reviews and the closing button are normal page content: just bring them into view. */
      var flowEl = el.closest('[data-hfs-reviews], [data-hfs-cta]');
      if (flowEl) { flowEl.scrollIntoView({ block: 'center' }); return; }
      var lens = (root.getAttribute('data-hfs-lengths') || '').split(',').map(function (v) { return parseFloat(v) || 0; });
      var phone = window.innerWidth < 750, vh = window.innerHeight;
      var cards = root.querySelectorAll('[data-hfs-card]');
      var DEF = [100, 90, 90, 120];
      var len = function (i) { return lens[i] > 0 ? lens[i] : DEF[i]; };
      var Lf = Math.max(40, len(phone ? 1 : 0)) / 100 * vh;
      var Lc = cards.length ? len(phone ? 3 : 2) / 100 * vh : 0;
      var at = -1, card = el.closest('[data-hfs-card]');
      if (card) {
        /* Where this card has fully landed (same timing as the entrances above). */
        var i = Array.prototype.indexOf.call(cards, card), n = cards.length;
        var w = phone ? 0.35 / (n - 0.65) : Math.min(0.3, 1 / n);
        var a = phone ? i / (n - 0.65) : (n > 1 ? i * (1 - w) / (n - 1) : 0);
        at = Lf + Lc * Math.min(0.999, a + w);
      } else if (el.closest('[data-hfs-cards]')) at = Lf + Lc * 0.999;
      if (at < 0) return;
      var top = root.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo(0, top + at);
    });
  }

  /* Theme blocks refresh individually, without a section:load. Remount the hero
     when its frame blocks change. Runs in the theme editor only. */
  if (window.Shopify && window.Shopify.designMode && window.MutationObserver) {
    var pending = null;
    new MutationObserver(function (records) {
      var roots = [];
      records.forEach(function (r) {
        /* Ignore the hero's own moves (Reviews/Closing button into the normal-page part):
           only real block edits should restart it. */
        if (r.target.closest && r.target.closest('[data-hfs-flow]')) return;
        var root = r.target.closest && r.target.closest('[data-hfs]');
        if (root && roots.indexOf(root) < 0) roots.push(root);
      });
      if (!roots.length) return;
      clearTimeout(pending);
      pending = setTimeout(function () {
        roots.forEach(function (root) { unmount(root); mount(root); });
      }, 150);
    }).observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', function (e) {
    mountAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-hfs]').forEach(unmount);
  });
})();
